/** Debounced + abortable autocomplete hook.
 *
 * Mirrors the RemoteAutocomplete debounce/abort pattern: each keystroke
 * schedules a fetch after DEBOUNCE_MS and aborts any prior in-flight request.
 * A no-op (empty, not loading) when no fetcher is configured.
 */
import { useEffect, useRef, useState } from 'react';

import type { Suggestion, SuggestionFetcher } from './types';

const DEBOUNCE_MS = 200;

export function useSuggestions(
  fetcher: SuggestionFetcher | undefined,
  query: string,
  extraFilterValues: Record<string, string>,
): { suggestions: Suggestion[]; loading: boolean } {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Stable string key so a fresh object literal each render doesn't refire.
  const extraKey = JSON.stringify(extraFilterValues);

  useEffect(() => {
    if (!fetcher) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const results = await fetcher(query.trim(), JSON.parse(extraKey), ctrl.signal);
        if (!ctrl.signal.aborted) setSuggestions(results);
      } catch {
        // Aborts + transient failures are swallowed; the next keystroke retries.
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [fetcher, query, extraKey]);

  // Abort any pending request on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { suggestions, loading };
}
